/**
 * Единый call куратора или практика: черновик, тост в ленте и карточка в теме стола.
 *
 * Один POST `/v1/me/call`. Шлюз пишет тост и сам шлёт в 5746. Отдельной
 * кнопки «поделиться» нет. Сокет ленты не трогаем — опрос раз в секунду.
 * `kind` в ответе: curator или practice — от этого строка тоста.
 */

import { Decimal } from 'decimal.js';
import { create } from 'zustand';

import { useSession } from '../access/session';
import { apiUrl } from '../lib/build';
import { marketLabel } from '../lib/protocol';
import { hrefOf, openTab } from './act';
import { putClip } from './clip';
import { saveLayout } from './deskLayout';
import { matchTitle } from './matchTitle';
import type { FeedRow } from './rows';
import { calcRowOf, forkFromCalc, planFromCalc } from './shareDesk';
import { useScreen } from './store';
import { useSettings } from '../store/settings';

export const POLL_MS = 1000;
const SEEN_KEY = 'forq.desk-call.seen';

export interface CallLeg {
  source: string;
  url: string;
  odd: string;
  title: string;
}

export type CallKind = 'curator' | 'practice';

export interface DeskCall {
  id: string;
  by: string;
  who: string;
  kind: CallKind;
  game: string;
  bet: string;
  forkId: string | null;
  legs: CallLeg[];
  at: number;
}

export interface DraftLeg {
  source: string;
  url: string;
  odd: string;
  title: string;
}

export interface Draft {
  game: string;
  bet: string;
  forkId: string | null;
  legs: DraftLeg[];
}

export function blankDraft(): Draft {
  return {
    game: '',
    bet: '',
    forkId: null,
    legs: [
      { source: '', url: '', odd: '', title: '' },
      { source: '', url: '', odd: '', title: '' },
    ],
  };
}

export function draftFromRow(row: FeedRow): Draft {
  const fork = row.row.fork;
  return {
    game: matchTitle(row),
    bet: marketLabel(fork.market, fork.sport),
    forkId: row.id,
    legs: row.row.legs.map((leg) => ({
      source: leg.source,
      url: hrefOf(row.id, leg) ?? '',
      odd: String(leg.odds ?? ''),
      title: [leg.name, leg.side].filter(Boolean).join(' · ') || leg.source,
    })),
  };
}

export function tapeFromRow(row: FeedRow): { fork: object; plan: object | null } {
  const fx = useSettings.getState().fx;
  const card = calcRowOf(row);
  return {
    fork: forkFromCalc(card),
    plan: planFromCalc(card, fx > 0 ? fx : 90),
  };
}

export function callBodyOf(
  draft: Draft,
  extra?: { fork?: object; plan?: object | null },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    game: draft.game.trim(),
    bet: draft.bet.trim(),
    forkId: draft.forkId,
    legs: draft.legs.map((leg) => ({
      source: leg.source.trim(),
      url: leg.url.trim(),
      odd: leg.odd.trim().replace(',', '.'),
      title: (leg.title || leg.source).trim(),
    })),
  };
  if (extra?.fork !== undefined) body.fork = extra.fork;
  if (extra?.plan != null) body.plan = extra.plan;
  return body;
}

export function draftReady(draft: Draft): string | null {
  if (!draft.game.trim()) return 'напиши матч';
  if (!draft.bet.trim()) return 'напиши ставку';
  if (draft.legs.length < 1) return 'нужна нога';
  for (const [i, leg] of draft.legs.entries()) {
    if (!leg.source.trim()) return `контора на ноге ${i + 1}`;
    if (!leg.url.trim()) return `ссылка на ноге ${i + 1}`;
    if (!/^https?:\/\//.test(leg.url.trim())) return `ссылка на ноге ${i + 1} не http`;
    if (!leg.odd.trim()) return `кэф на ноге ${i + 1}`;
    if (!/^\d+([.,]\d+)?$/.test(leg.odd.trim())) return `кэф на ноге ${i + 1} не число`;
  }
  return null;
}

export function readCall(raw: unknown): DeskCall | null {
  if (raw === null || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.id !== 'string' || !row.id) return null;
  if (typeof row.who !== 'string' || !row.who) return null;
  if (typeof row.game !== 'string' || typeof row.bet !== 'string') return null;
  if (!Array.isArray(row.legs) || row.legs.length === 0) return null;
  const legs: CallLeg[] = [];
  for (const item of row.legs) {
    if (item === null || typeof item !== 'object') return null;
    const leg = item as Record<string, unknown>;
    if (typeof leg.source !== 'string' || typeof leg.url !== 'string') return null;
    if (typeof leg.odd !== 'string' || typeof leg.title !== 'string') return null;
    legs.push({
      source: leg.source,
      url: leg.url,
      odd: leg.odd,
      title: leg.title,
    });
  }
  return {
    id: row.id,
    by: typeof row.by === 'string' ? row.by : '',
    who: row.who,
    kind: row.kind === 'practice' ? 'practice' : 'curator',
    game: row.game,
    bet: row.bet,
    forkId: typeof row.forkId === 'string' && row.forkId ? row.forkId : null,
    legs,
    at: typeof row.at === 'number' ? row.at : 0,
  };
}

export function toastLine(call: DeskCall): string {
  const from = call.kind === 'practice' ? 'практика' : 'куратора';
  return `call от ${from} · ${call.who}`;
}

/** Крупный плюс на тосте. `roi` с кадра — проценты, не доля. */
export function toastRoi(roi: string | number | null | undefined): string | null {
  if (roi === null || roi === undefined || roi === '') return null;
  try {
    const n = new Decimal(roi);
    if (!n.isFinite()) return null;
    const sign = n.isNeg() ? '−' : '+';
    const body = n.abs().toDecimalPlaces(2).toFixed(2).replace(/\.?0+$/, '');
    return `${sign} ${body}%`;
  } catch {
    return null;
  }
}

function seenId(): string | null {
  try {
    return sessionStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

function markSeen(id: string): void {
  try {
    sessionStorage.setItem(SEEN_KEY, id);
  } catch {
    // нет хранилища — тост просто придёт ещё раз
  }
}

export function openCall(call: DeskCall): void {
  for (const leg of call.legs) {
    if (leg.url) openTab(leg.url, leg.source);
  }
}

function booksKey(codes: readonly string[]): string {
  return codes.filter((code) => code !== '').sort().join('\0');
}

function forkBooksKey(fork: { id?: string; legs?: readonly { s?: string }[] }): string {
  return booksKey((fork.legs ?? []).map((leg) => leg.s ?? ''));
}

/** Живая вилка в этом кадре: свой id, иначе те же конторы (id с чужой ленты не берём). */
export function deskForkId(call: DeskCall): string | null {
  const screen = useScreen.getState();
  if (call.forkId && screen.forks.has(call.forkId)) return call.forkId;
  if (call.forkId && screen.hiddenForks.has(call.forkId)) return call.forkId;
  if (call.forkId && screen.ghosts.has(call.forkId)) return call.forkId;
  const want = booksKey(call.legs.map((leg) => leg.source));
  if (want) {
    for (const fork of screen.forks.values()) {
      if (forkBooksKey(fork) === want) return fork.id;
    }
    for (const hid of screen.hiddenForks.values()) {
      if (forkBooksKey(hid.fork) === want) return hid.fork.id;
    }
  }
  return call.forkId;
}

/** Стол сверху: показать панель, положить вилку, прокрутить к калькулятору. */
export function revealDesk(id: string): void {
  if (id === '') return;
  saveLayout({ showCalculator: true });
  putClip(id);
  const jump = (): void => {
    document.querySelector('.nx-clips')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  jump();
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => requestAnimationFrame(jump));
  }
}

/** Значок калькулятора на тосте — вилка на стол, без карточки и без касс. */
export function openCallInCalc(call: DeskCall): boolean {
  const id = deskForkId(call);
  if (id === null) return false;
  revealDesk(id);
  return true;
}

/**
 * «открыть» на тосте — вилка в ленте и калькулятор, не кассы.
 * Кассы человек откроет сам кнопкой «открыть обе».
 */
export function openCallInFeed(call: DeskCall): boolean {
  const id = deskForkId(call);
  if (id === null) return false;
  revealDesk(id);
  const screen = useScreen.getState();
  screen.setSel(id);
  screen.setOpen(id);
  return true;
}

export interface DeskCallState {
  draft: Draft | null;
  inbox: DeskCall | null;
  error: string | null;
  tapeNote: string | null;
  tapeHold: { fork: object; plan: object | null } | null;
  sending: boolean;
  openFork: (row: FeedRow) => void;
  openHand: () => void;
  setDraft: (next: Draft) => void;
  close: () => void;
  send: () => Promise<void>;
  sendCalc: (row: FeedRow) => Promise<void>;
  dismiss: () => void;
  hear: (call: DeskCall | null) => void;
}

async function postCall(
  draft: Draft,
  extra?: { fork?: object; plan?: object | null },
): Promise<{ call: DeskCall | null; error: string | null; tapeNote: string | null }> {
  const res = await fetch(apiUrl('/v1/me/call'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(callBodyOf(draft, extra)),
  });
  if (res.status === 429) return { call: null, error: 'подожди секунду', tapeNote: null };
  if (!res.ok) return { call: null, error: 'не ушло', tapeNote: null };
  const body = (await res.json()) as { call?: unknown; tape?: { ok?: unknown; detail?: unknown } };
  const call = readCall(body.call);
  const tape = body.tape;
  const tapeNote =
    tape && tape.ok === false && typeof tape.detail === 'string' && tape.detail.trim() !== ''
      ? tape.detail
      : null;
  return { call, error: null, tapeNote };
}

export const useDeskCall = create<DeskCallState>((set, get) => ({
  draft: null,
  inbox: null,
  error: null,
  tapeNote: null,
  tapeHold: null,
  sending: false,

  openFork: (row) =>
    set({ draft: draftFromRow(row), tapeHold: tapeFromRow(row), error: null, tapeNote: null }),
  openHand: () => set({ draft: blankDraft(), tapeHold: null, error: null, tapeNote: null }),
  setDraft: (next) => set({ draft: next, error: null }),
  close: () => set({ draft: null, tapeHold: null, error: null }),

  send: async () => {
    const draft = get().draft;
    if (draft === null) return;
    const why = draftReady(draft);
    if (why !== null) {
      set({ error: why });
      return;
    }
    if (get().sending) return;
    set({ sending: true, error: null, tapeNote: null });
    try {
      const got = await postCall(draft, get().tapeHold ?? undefined);
      if (got.error !== null) {
        set({ error: got.error, sending: false });
        return;
      }
      set({ draft: null, sending: false, error: null, tapeNote: got.tapeNote });
      if (got.call !== null) {
        markSeen(got.call.id);
        set({ inbox: got.call });
      }
    } catch {
      set({ error: 'нет связи', sending: false });
    }
  },

  sendCalc: async (row) => {
    const card = calcRowOf(row);
    const draft = draftFromRow(card);
    const why = draftReady(draft);
    if (why !== null) {
      set({ error: why, tapeNote: why });
      return;
    }
    if (get().sending) return;
    set({ sending: true, error: null, tapeNote: null });
    try {
      const tape = tapeFromRow(row);
      const got = await postCall(draft, tape);
      if (got.error !== null) {
        set({ error: got.error, tapeNote: got.error, sending: false });
        return;
      }
      set({ sending: false, error: null, tapeNote: got.tapeNote ?? 'отправили' });
      if (got.call !== null) {
        markSeen(got.call.id);
        set({ inbox: got.call });
      }
    } catch {
      set({ error: 'нет связи', tapeNote: 'нет связи', sending: false });
    }
  },

  dismiss: () => {
    const inbox = get().inbox;
    if (inbox !== null) markSeen(inbox.id);
    set({ inbox: null });
  },

  hear: (call) => {
    if (call === null) return;
    if (seenId() === call.id) return;
    set({ inbox: call });
  },
}));

export async function pullCall(): Promise<DeskCall | null> {
  const me = useSession.getState().me;
  if (!me.authenticated || !me.access) return null;
  const res = await fetch(apiUrl('/v1/me/call'), { credentials: 'same-origin' });
  if (!res.ok) return null;
  const body = (await res.json()) as { call?: unknown };
  return readCall(body.call ?? null);
}
